import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { UserPlant } from '../types/garden';
import { Edit2, Trash2, Loader2, Calendar as CalendarIcon, Sprout, ChevronDown, ChevronUp, Info, Shovel } from 'lucide-react';
import { supabase, retryOperation } from '../lib/supabase';
import { normalizeDate, formatDateRange, isFrostSensitive, isColdHardy, getDaysBetweenDates } from '../utils/dateUtils';

interface GardenPlantCardProps {
  userPlant: UserPlant;
  onEdit: (plant: UserPlant) => void;
  onDelete: (plantId: string) => void;
}

export default function GardenPlantCard({ userPlant, onEdit, onDelete }: GardenPlantCardProps) {
  const { plant } = userPlant;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [success_notes, setSuccessNotes] = useState(userPlant.success_notes || '');
  const [varieties_notes, setVarietiesNotes] = useState(userPlant.varieties_notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Debug logging for cold hardy plants
    if (isColdHardy(plant.frost_tolerance)) {
      console.log(`COLD HARDY PLANT DEBUG - ${plant.name}:`, {
        frost_tolerance: plant.frost_tolerance,
        transplant_days_before_frost: plant.transplant_cold_hardy_days_before_frost_max,
        direct_sow_days_before_frost: plant.direct_sow_cold_hardy_days_before_frost_max,
        last_frost_date: userPlant.last_frost_date,
        sow_indoors_start_date: userPlant.sow_indoors_start_date,
        sow_indoors_end_date: userPlant.sow_indoors_end_date,
        transplant_start_date: userPlant.transplant_start_date,
        transplant_end_date: userPlant.transplant_end_date,
        direct_sow_start_date: userPlant.direct_sow_start_date,
        direct_sow_end_date: userPlant.direct_sow_end_date
      });
    }
  }, [plant, userPlant]);

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to remove ${plant.name} from your garden?`)) {
      setIsDeleting(true);
      setError(null);
      try {
        await onDelete(userPlant.id);
      } catch (err) {
        setError('Failed to remove plant. Please try again.');
        console.error('Error deleting plant:', err);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    let retryCount = 0;
    const maxRetries = 3;

    const trySave = async () => {
      try {
        const { error: saveError } = await retryOperation(() =>
          supabase
            .from('user_gardens')
            .update({
              success_notes,
              varieties_notes,
              updated_at: new Date().toISOString()
            })
            .eq('id', userPlant.id)
        );

        if (saveError) throw saveError;
        setIsEditing(false);
        setShowDetails(true); // Keep details expanded after saving
      } catch (err) {
        if (retryCount < maxRetries) {
          retryCount++;
          // Exponential backoff
          const delay = Math.pow(2, retryCount) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          return trySave();
        }
        throw err;
      }
    };

    try {
      await trySave();
    } catch (err) {
      console.error('Error saving notes:', err);
      setError('Failed to save notes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    setShowDetails(true); // Auto-expand details when editing
    setError(null);
  };

  const getSowingTiming = () => {
    if (!userPlant.sow_indoors_start_date || !userPlant.sow_indoors_end_date) {
      return 'Not set';
    }

    return formatDateRange(userPlant.sow_indoors_start_date, userPlant.sow_indoors_end_date);
  };

  const getTransplantTiming = () => {
    if (!userPlant.transplant_start_date || !userPlant.transplant_end_date) {
      return 'Not set';
    }

    // Use the normalized date strings directly without creating Date objects
    // that would introduce timezone issues
    return formatDateRange(userPlant.transplant_start_date, userPlant.transplant_end_date);
  };

  const getDirectSowTiming = () => {
    if (!userPlant.direct_sow_start_date || !userPlant.direct_sow_end_date) {
      return 'Not set';
    }

    return formatDateRange(userPlant.direct_sow_start_date, userPlant.direct_sow_end_date);
  };

  // Calculate days before last frost for transplanting
  const getTransplantDaysBeforeFrost = () => {
    if (!userPlant.transplant_start_date || !userPlant.last_frost_date) {
      return null;
    }
    
    const days = getDaysBetweenDates(userPlant.transplant_start_date, userPlant.last_frost_date);
    if (days === 0) {
      return "On frost date";
    } else if (days && days > 0) {
      return `${days} days before last frost`;
    } else {
      return "After frost date";
    }
  };

  // Calculate days before last frost for direct sowing
  const getDirectSowDaysBeforeFrost = () => {
    if (!userPlant.direct_sow_start_date || !userPlant.last_frost_date) {
      return null;
    }
    
    const days = getDaysBetweenDates(userPlant.direct_sow_start_date, userPlant.last_frost_date);
    if (days === 0) {
      return "On frost date";
    } else if (days && days > 0) {
      return `${days} days before last frost`;
    } else {
      return "After frost date";
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col">
      <Link to={`/plants/${plant.id}`} className="block">
        <div className="relative w-full pt-[60%]">
          <div className={`absolute inset-0 bg-gray-100 transition-opacity duration-300 ${imageLoaded ? 'opacity-0' : 'opacity-100'}`} />
          <img 
            src={plant.image_url} 
            alt={plant.name}
            onLoad={() => setImageLoaded(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
          {isColdHardy(plant.frost_tolerance) && (
            <div className="absolute top-2 right-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
              Cold Hardy
            </div>
          )}
          {isFrostSensitive(plant.frost_tolerance) && (
            <div className="absolute top-2 right-2 bg-orange-100 text-orange-800 text-xs font-medium px-2 py-1 rounded-full">
              Frost Sensitive
            </div>
          )}
        </div>
      </Link>
      
      <div className="p-3 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <Link to={`/plants/${plant.id}`}>
            <h3 className="font-semibold text-base">{plant.name}</h3>
          </Link>
        </div>

        {/* Timing Information */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-1.5 text-sm">
            <CalendarIcon className="h-4 w-4 text-gray-500" />
            <div>
              <span className="text-gray-600 font-medium">Sow Indoors:</span>
              <span className="ml-1 text-gray-600">
                {getSowingTiming()}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <Shovel className="h-4 w-4 text-gray-500" />
            <div>
              <span className="text-gray-600 font-medium">Direct Sow:</span>
              <span className="ml-1 text-gray-600">
                {getDirectSowTiming()}
                {userPlant.direct_sow_start_date && userPlant.last_frost_date && (
                  <span className="ml-1 text-xs text-gray-500">
                    ({getDirectSowDaysBeforeFrost()})
                  </span>
                )}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <Sprout className="h-4 w-4 text-gray-500" />
            <div>
              <span className="text-gray-600 font-medium">Transplant Outdoors:</span>
              <span className="ml-1 text-gray-600">
                {getTransplantTiming()}
                {userPlant.transplant_start_date && userPlant.last_frost_date && (
                  <span className="ml-1 text-xs text-gray-500">
                    ({getTransplantDaysBeforeFrost()})
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* View Details Button */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex-1 text-sm text-primary-600 hover:text-primary-700 flex items-center justify-center gap-1"
          >
            {showDetails ? (
              <>
                <ChevronUp className="h-4 w-4" />
                <span>Hide Details</span>
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                <span>View Details</span>
              </>
            )}
          </button>
          
          <button
            onClick={() => setShowDebug(!showDebug)}
            className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
            title="Debug Info"
          >
            <Info className="h-4 w-4" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-3 text-sm text-red-600 text-center">
            {error}
          </div>
        )}

        {/* Collapsible Details */}
        {showDetails && (
          <div className="space-y-3 mb-3">
            {isEditing ? (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={success_notes}
                    onChange={(e) => setSuccessNotes(e.target.value)}
                    className="w-full px-2 py-1 border rounded-md text-sm"
                    rows={2}
                    placeholder="Add notes..."
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">My Varieties</label>
                  <textarea
                    value={varieties_notes}
                    onChange={(e) => setVarietiesNotes(e.target.value)}
                    className="w-full px-2 py-1 border rounded-md text-sm"
                    rows={2}
                    placeholder="Note varieties..."
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Notes</h4>
                  {success_notes ? (
                    <p className="text-sm text-gray-600">{success_notes}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No notes added yet</p>
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">My Varieties</h4>
                  {varieties_notes ? (
                    <p className="text-sm text-gray-600">{varieties_notes}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No varieties noted yet</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Debug Panel */}
        {showDebug && (
          <div className="bg-gray-100 p-2 mt-2 text-xs font-mono overflow-x-auto">
            <h4 className="font-bold">Plant Debug:</h4>
            <div>Plant: {plant.name}</div>
            <div>Frost Tolerance: {plant.frost_tolerance}</div>
            <div>Last Frost Date: {userPlant.last_frost_date}</div>
            <div>Time to Maturity: {plant.time_seed_to_maturity_max || 'Not set'} days</div>
            <div>Harvest Window: {plant.harvest_window_max || 'Not set'} days</div>
            <div>Direct Sow Days Before Frost: {plant.direct_sow_cold_hardy_days_before_frost_max || 'Not set'}</div>
            <div>Transplant Days Before Frost: {plant.transplant_cold_hardy_days_before_frost_max || 'Not set'}</div>
            <div className="text-blue-600">Direct Sow: {userPlant.direct_sow_start_date} to {userPlant.direct_sow_end_date}</div>
            <div className="text-blue-600">Transplant: {userPlant.transplant_start_date} to {userPlant.transplant_end_date}</div>
            <div className="text-blue-600">Indoor Sow: {userPlant.sow_indoors_start_date} to {userPlant.sow_indoors_end_date}</div>
            {userPlant.direct_sow_start_date !== null && userPlant.last_frost_date !== null && (
              <div className="text-blue-600">Days before frost (direct): {getDaysBetweenDates(userPlant.direct_sow_start_date, userPlant.last_frost_date)}</div>
            )}
            {userPlant.transplant_start_date !== null && userPlant.last_frost_date !== null && (
              <div className="text-blue-600">Days before frost (transplant): {getDaysBetweenDates(userPlant.transplant_start_date, userPlant.last_frost_date)}</div>
            )}
          </div>
        )}

        <div className="mt-auto flex justify-end gap-2">
          {isEditing ? (
            <>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setError(null);
                  // Reset to original values
                  setSuccessNotes(userPlant.success_notes || '');
                  setVarietiesNotes(userPlant.varieties_notes || '');
                }}
                className="px-2 py-1 text-xs text-gray-600 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-2 py-1 text-xs text-primary-600 hover:text-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Save'
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleStartEditing}
                className="p-1.5 text-gray-600 hover:text-primary-600 transition-colors"
                title="Edit notes"
              >
                <Edit2 className="h-4 w-4" />
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="p-1.5 text-gray-600 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Remove from garden"
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}